import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatDateTime } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Mail, Check, AlertCircle, Trash2, Inbox, RefreshCw, Copy, X } from "lucide-react";

export const AccountSection = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ gmail_user: "", gmail_app_password: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [inbound, setInbound] = useState(null);
  const [inForm, setInForm] = useState({ email: "", app_password: "", forward_address: "" });
  const [inSaving, setInSaving] = useState(false);
  const [inError, setInError] = useState("");
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirming(true);
    try {
      await confirm.onConfirm();
      setConfirm(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setConfirming(false); }
  };

  const load = async () => {
    const { data } = await api.get("/users/me/gmail");
    setCfg(data);
    if (data.gmail_user) setForm((f) => ({ ...f, gmail_user: data.gmail_user }));
  };
  const loadInbound = async () => {
    const { data } = await api.get("/inbound/config");
    setInbound(data);
    setInForm((f) => ({ ...f, email: data.email || "", forward_address: data.forward_address || "" }));
  };
  useEffect(() => {
    load();
    if (isAdmin) loadInbound();
  }, [isAdmin]);

  const saveInbound = async (e) => {
    e.preventDefault();
    setInError(""); setInSaving(true);
    try {
      await api.post("/inbound/config", inForm);
      setInForm((f) => ({ ...f, app_password: "" }));
      await loadInbound();
    } catch (err) {
      setInError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setInSaving(false); }
  };

  const clearInbound = () => setConfirm({
    title: "Desativar receção por email",
    message: "Tem a certeza que quer desativar a receção de faturas por email? Os clientes deixarão de poder reencaminhar faturas.",
    onConfirm: async () => {
      await api.delete("/inbound/config");
      setInForm({ email: "", app_password: "", forward_address: "" });
      await loadInbound();
      toast.success("Receção por email desativada.");
    },
  });

  const pollNow = async () => {
    setPolling(true); setPollMsg("");
    try {
      const { data } = await api.post("/inbound/poll");
      setPollMsg(`${data.emails} email(s) lidos · ${data.created} fatura(s) criada(s) · ${data.ignored} ignorado(s).`);
      await loadInbound();
    } catch (err) {
      setPollMsg(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setPolling(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setSaving(true);
    try {
      await api.post("/users/me/gmail", form);
      setSuccess("Gmail configurado com sucesso!");
      setForm((f) => ({ ...f, gmail_app_password: "" }));
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const clear = () => setConfirm({
    title: "Remover configuração Gmail",
    message: "Tem a certeza que quer remover a configuração Gmail? Deixará de poder enviar convites e faturas para o TocOnline a partir desta conta.",
    onConfirm: async () => {
      await api.delete("/users/me/gmail");
      await load();
      setForm({ gmail_user: "", gmail_app_password: "" });
      toast.success("Configuração Gmail removida.");
    },
  });

  return (
    <div className="max-w-2xl" data-testid="account-section">
      <div className="card-soft">
        <div className="flex items-center justify-between">
          <div>
            <span className="label-eyebrow">gmail workspace</span>
            <h2 className="font-display text-xl mt-1">Email de envio</h2>
            <p className="text-sm text-[color:var(--ink-muted)] mt-1">Usado para enviar convites e faturas para o TocOnline em seu nome.</p>
          </div>
          {cfg?.configured && (
            <span className="chip chip-processed inline-flex items-center gap-1" data-testid="gmail-status-configured"><Check size={12}/> Configurado</span>
          )}
        </div>

        <form onSubmit={save} className="mt-5 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">Endereço Gmail Workspace</label>
            <input type="email" className="input-soft" value={form.gmail_user} onChange={(e) => setForm({...form, gmail_user: e.target.value})} required data-testid="gmail-user-input" placeholder="nome@perfinco.pt" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">App Password (16 caracteres)</label>
            <input type="password" className="input-soft font-mono-data" value={form.gmail_app_password} onChange={(e) => setForm({...form, gmail_app_password: e.target.value})} placeholder={cfg?.configured ? "••••••••••••••••" : "abcd efgh ijkl mnop"} required data-testid="gmail-password-input" />
            <p className="text-xs text-[color:var(--ink-muted)] mt-2">
              Gere uma App Password em <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[color:var(--primary)] underline">myaccount.google.com/apppasswords</a> (precisa de 2FA ativo).
            </p>
          </div>

          {error && (
            <div className="text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3 inline-flex items-start gap-2" data-testid="settings-error">
              <AlertCircle size={16} className="flex-none mt-0.5" /><span>{error}</span>
            </div>
          )}
          {success && (
            <div className="text-sm text-[#065F46] bg-[#D1FAE5] border border-[#A7F3D0] rounded-lg p-3 inline-flex items-start gap-2" data-testid="settings-success">
              <Check size={16} className="flex-none mt-0.5" /><span>{success}</span>
            </div>
          )}

          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-gmail-btn">
              <Mail size={16} /> {saving ? "A verificar…" : "Guardar e testar"}
            </button>
            {cfg?.configured && (
              <button type="button" onClick={clear} className="btn-secondary text-[#991B1B] inline-flex items-center gap-2" data-testid="clear-gmail-btn">
                <Trash2 size={14} /> Remover
              </button>
            )}
          </div>
        </form>
      </div>

      {isAdmin && (
        <div className="card-soft mt-6" data-testid="inbound-card">
          <div className="flex items-center justify-between">
            <div>
              <span className="label-eyebrow">receção por email</span>
              <h2 className="font-display text-xl mt-1">Faturas reencaminhadas por email</h2>
              <p className="text-sm text-[color:var(--ink-muted)] mt-1">Caixa de correio (Gmail) onde os clientes reencaminham faturas. O sistema lê os anexos (imagens e PDF) e associa-os ao cliente pelo email de quem reencaminha. <strong>Pode usar um alias:</strong> faça login com a conta principal e indique o alias como endereço de reencaminhamento.</p>
            </div>
            {inbound?.configured && (
              <span className="chip chip-processed inline-flex items-center gap-1" data-testid="inbound-status-configured"><Check size={12}/> Ativo</span>
            )}
          </div>

          {inbound?.configured && (
            <div className="mt-4 bg-[color:var(--paper)] border border-[color:var(--border)] rounded-lg p-3" data-testid="inbound-forward-info">
              <div className="label-eyebrow mb-1">Endereço para os clientes reencaminharem</div>
              <div className="flex items-center gap-2">
                <span className="flex-1 font-mono-data text-sm break-all">{inbound.forward_address || inbound.email}</span>
                <button type="button" onClick={() => navigator.clipboard.writeText(inbound.forward_address || inbound.email)} className="btn-secondary text-xs inline-flex items-center gap-1"><Copy size={13}/> Copiar</button>
              </div>
              <div className="text-xs text-[color:var(--ink-muted)] mt-1">Leitura via conta: <span className="font-mono-data">{inbound.email}</span></div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button type="button" onClick={pollNow} disabled={polling} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="poll-inbound-btn">
                  <RefreshCw size={14} className={polling ? "animate-spin" : ""} /> {polling ? "A verificar…" : "Verificar agora"}
                </button>
                {inbound.last_poll && (
                  <span className="text-xs text-[color:var(--ink-muted)]">Última verificação: {formatDateTime(inbound.last_poll)}</span>
                )}
              </div>
              {pollMsg && <p className="text-xs text-[color:var(--primary)] mt-2" data-testid="poll-result">{pollMsg}</p>}
            </div>
          )}

          <form onSubmit={saveInbound} className="mt-5 space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">Conta de login (conta principal, não o alias)</label>
              <input type="email" className="input-soft" value={inForm.email} onChange={(e) => setInForm({...inForm, email: e.target.value})} required data-testid="inbound-email-input" placeholder="conta.principal@perfinco.pt" />
              <p className="text-xs text-[color:var(--ink-muted)] mt-1">A conta real cujas credenciais usamos para ler a caixa. Um alias não tem login próprio.</p>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Endereço de reencaminhamento (alias) — opcional</label>
              <input type="email" className="input-soft" value={inForm.forward_address} onChange={(e) => setInForm({...inForm, forward_address: e.target.value})} data-testid="inbound-forward-input" placeholder="faturas@perfinco.pt" />
              <p className="text-xs text-[color:var(--ink-muted)] mt-1">O endereço que mostramos aos clientes. Se usar um alias, só processamos emails enviados para ele. Deixe vazio para usar a conta de login.</p>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">App Password (16 caracteres) — da conta de login</label>
              <input type="password" className="input-soft font-mono-data" value={inForm.app_password} onChange={(e) => setInForm({...inForm, app_password: e.target.value})} placeholder={inbound?.configured ? "••••••••••••••••" : "abcd efgh ijkl mnop"} required data-testid="inbound-password-input" />
              <p className="text-xs text-[color:var(--ink-muted)] mt-2">Ative o IMAP no Gmail e gere uma App Password em <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[color:var(--primary)] underline">myaccount.google.com/apppasswords</a> (precisa de 2FA).</p>
            </div>
            {inError && (
              <div className="text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3 inline-flex items-start gap-2" data-testid="inbound-error">
                <AlertCircle size={16} className="flex-none mt-0.5" /><span>{inError}</span>
              </div>
            )}
            <div className="flex gap-2">
              <button type="submit" disabled={inSaving} className="btn-primary inline-flex items-center gap-2" data-testid="save-inbound-btn">
                <Inbox size={16} /> {inSaving ? "A verificar…" : "Guardar e testar"}
              </button>
              {inbound?.configured && (
                <button type="button" onClick={clearInbound} className="btn-secondary text-[#991B1B] inline-flex items-center gap-2" data-testid="clear-inbound-btn">
                  <Trash2 size={14} /> Desativar
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 fade-in" onClick={() => !confirming && setConfirm(null)}>
          <div className="card-soft w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="settings-confirm-modal">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">{confirm.title}</span>
              <button onClick={() => setConfirm(null)} className="p-1 rounded hover:bg-[color:var(--border)]"><X size={16} /></button>
            </div>
            <p className="text-sm text-[color:var(--ink-muted)] mt-3">{confirm.message}</p>
            <div className="mt-5 flex gap-2">
              <button onClick={runConfirm} disabled={confirming} className="btn-primary flex-1 bg-[#991B1B] hover:bg-[#7F1515] border-[#991B1B]" data-testid="settings-confirm-btn">
                {confirming ? "A processar…" : "Confirmar"}
              </button>
              <button onClick={() => setConfirm(null)} disabled={confirming} className="btn-secondary" data-testid="settings-cancel-btn">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
